import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import API from "../api/api";

const GlobalLoaderContext = createContext({ loading: false });

export function GlobalLoaderProvider({ children }) {
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const inited = useRef(false);
  const hideTimerRef = useRef(null);
  const HIDE_DELAY_MS = Number(process.env.REACT_APP_LOADER_DELAY_MS || 50);

  // Attach interceptors before child useEffect data calls start on refresh.
  useLayoutEffect(() => {
    if (inited.current) return;
    inited.current = true;

    const inc = () => setPending((p) => p + 1);
    const dec = () => setPending((p) => Math.max(0, p - 1));

    const onReq = (config) => {
      if (!config?.skipLoader) inc();
      return config;
    };
    const onReqErr = (error) => {
      dec();
      return Promise.reject(error);
    };
    const onRes = (res) => {
      if (!res?.config?.skipLoader) dec();
      return res;
    };
    const onResErr = (error) => {
      if (!error?.config?.skipLoader) dec();
      return Promise.reject(error);
    };

    const apiReqId = API.interceptors.request.use(onReq, onReqErr);
    const apiResId = API.interceptors.response.use(onRes, onResErr);

    const axReqId = axios.interceptors.request.use(onReq, onReqErr);
    const axResId = axios.interceptors.response.use(onRes, onResErr);

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      inc();
      try {
        return await originalFetch(...args);
      } finally {
        dec();
      }
    };

    return () => {
      API.interceptors.request.eject(apiReqId);
      API.interceptors.response.eject(apiResId);
      axios.interceptors.request.eject(axReqId);
      axios.interceptors.response.eject(axResId);
      window.fetch = originalFetch;
    };
  }, []);

  useEffect(() => {
    if (pending > 0) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      setLoading(true);
      return;
    }

    hideTimerRef.current = setTimeout(() => {
      setLoading(false);
      hideTimerRef.current = null;
    }, HIDE_DELAY_MS);

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [pending, HIDE_DELAY_MS]);

  const value = useMemo(() => ({ loading }), [loading]);
  return <GlobalLoaderContext.Provider value={value}>{children}</GlobalLoaderContext.Provider>;
}

export function useGlobalLoader() {
  return useContext(GlobalLoaderContext);
}